import { Children, ReactElement, useEffect, useState } from "react";
import { LuArrowRight, LuChevronLeft, LuChevronRight } from "react-icons/lu";
import { useNavigate } from "react-router-dom";
import Slider from "react-slick";
import "slick-carousel/slick/slick-theme.css";
import "slick-carousel/slick/slick.css";
import darvinSeeAllArticles from "../../../assets/darvin/darvin_see_all_articles.jpg";
import { useIsCompactView } from "../../../core_stores/useIsCompactViewStore";

interface BlogPost {
  slug: string;
  title: string;
  date: string;
  excerpt: string;
  coverImage: string;
  dateFolder: string;
}

export function WelcomeBlogFeed() {
  const isCompactView = useIsCompactView();
  const [blogPosts, setBlogPosts] = useState<BlogPost[] | null>(null);

  useEffect(() => {
    const fetchBlogPosts = async () => {
      try {
        const response = await fetch("/blog/index.json");
        if (!response.ok) {
          throw new Error("Failed to load blog posts");
        }
        const posts = await response.json();
        setBlogPosts(posts);
      } catch (error) {
        console.error("Error loading blog posts:", error);
        setBlogPosts([]);
      }
    };

    fetchBlogPosts();
  }, []);

  const getContent = () => {
    return isCompactView ? <CompactFeed posts={blogPosts} /> : <DefaultFeed posts={blogPosts} />;
  };

  if (blogPosts === null) {
    return <div className="h-8"></div>;
  }

  return <div className="w-full py-8">{getContent()}</div>;
}

function CompactFeed({ posts }: { posts: BlogPost[] | null }) {
  var settings = {
    dots: true,
    appendDots: (dots: React.ReactNode) => {
      return (
        <div>
          {Children.map(
            dots as unknown as ReactElement[],
            (dot: ReactElement<{ className: string }>) => {
              const isActive = dot?.props.className.includes("slick-active");

              return (
                <div
                  className={`h-3 w-3 rounded-full
                ${
                  isActive
                    ? "bg-[#8B8B8B] dark:bg-[#8B8B8B]"
                    : "bg-[#989CA933] dark:bg-[#181A1D]"
                }`}
                />
              );
            }
          )}
        </div>
      );
    },
    dotsClass: "flex items-center justify-center space-x-2 pt-6",
    arrows: false,
    infinite: false,
    speed: 500,
    slidesToShow: 1,
    slidesToScroll: 1,
  };

  return (
    <Slider {...settings}>
      {posts!.map((post) => (
        <BlogPostCard key={post.slug} post={post} />
      ))}
      <ReadMoreButton key="read-more" />
    </Slider>
  );
}

function DefaultFeed({ posts }: { posts: BlogPost[] | null }) {
  const postsPerPage = 3;

  const [currentPage, setCurrentPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [itemsOnCurrentPage, setItemsOnCurrentPage] = useState<ReactElement[]>(
    []
  );

  useEffect(() => {
    if (posts) {
      setTotalPages(Math.ceil(posts.length / postsPerPage));
      const postsOnCurrentPage = posts.slice(
        currentPage * postsPerPage,
        (currentPage + 1) * postsPerPage
      );

      updateItemsOnCurrentPage(postsOnCurrentPage);
    }
  }, [posts]);

  const goToPreviousPage = () => {
    setCurrentPage(currentPage - 1);
    const postsOnCurrentPage = posts!.slice(
      (currentPage - 1) * postsPerPage,
      currentPage * postsPerPage
    );

    updateItemsOnCurrentPage(postsOnCurrentPage);
  };

  const goToNextPage = () => {
    setCurrentPage(currentPage + 1);
    const postsOnCurrentPage = posts!.slice(
      (currentPage + 1) * postsPerPage,
      (currentPage + 2) * postsPerPage
    );

    updateItemsOnCurrentPage(postsOnCurrentPage);
  };

  const updateItemsOnCurrentPage = (postsOnCurrentPage: BlogPost[]) => {
    const itemsOnCurrentPage = postsOnCurrentPage.map((post) => (
      <BlogPostCard key={post.slug} post={post} />
    ));

    if (itemsOnCurrentPage.length % 3 !== 0) {
      itemsOnCurrentPage.push(<ReadMoreButton key="read-more" />);
    }

    setItemsOnCurrentPage(itemsOnCurrentPage);
  };

  return (
    <>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {itemsOnCurrentPage}
      </div>

      <div className="flex justify-end items-center mt-8 px-4 max-w-6xl mx-auto">
        <div className="flex items-center gap-4">
          <PaginationControls
            currentPage={currentPage}
            totalPages={totalPages}
            totalPosts={posts?.length || 0}
            goToPreviousPage={goToPreviousPage}
            goToNextPage={goToNextPage}
          />
        </div>
      </div>
    </>
  );
}

function PaginationControls({
  currentPage,
  totalPages,
  totalPosts,
  goToPreviousPage,
  goToNextPage,
}: {
  currentPage: number;
  totalPages: number;
  totalPosts: number;
  goToPreviousPage: () => void;
  goToNextPage: () => void;
}) {
  return (
    <div className="flex items-center gap-4">
      <div>
        <button
          onClick={goToPreviousPage}
          disabled={currentPage === 0}
          className="p-2 bg-[#3035350F] dark:bg-[#24282B38]
      rounded-l-md disabled:opacity-100
      hover:bg-[#30353544] dark:hover:bg-[#303535AA]
      transition-colors duration-300"
          title="Previous page"
        >
          <LuChevronLeft
            size={20}
            className={`${currentPage === 0 ? "opacity-50" : ""}`}
          />
        </button>

        <button
          onClick={goToNextPage}
          disabled={currentPage >= totalPages - 1}
          className="p-2 bg-[#3035350F] dark:bg-[#30353544]
      rounded-r-md disabled:opacity-100
      hover:bg-[#30353544] dark:hover:bg-[#303535AA]
      transition-colors duration-300"
          title="Next page"
        >
          <LuChevronRight
            size={20}
            className={`${currentPage >= totalPages - 1 ? "opacity-50" : ""}`}
          />
        </button>
      </div>

      <span
        className="text-sm font-[600]
    text-light-placeholder dark:text-dark-placeholder
    min-w-[60px] text-center"
      >
        {totalPosts > 0
          ? `${currentPage + 1}/${totalPages} Pages`
          : "No posts"}
      </span>
    </div>
  );
}

function BlogPostCard({ post }: { post: BlogPost }) {
  const isCompactView = useIsCompactView();
  const navigate = useNavigate();

  return (
    <div
      id={`home-blog-post-card-${post.slug}`}
      className={`bg-gradient-to-b from-[#EEEFF6] dark:from-[#30353A]
      ${isCompactView ? "mx-2" : ""}
      to-white dark:to-[#222528] p-2
      flex flex-col h-[300px]
      rounded-lg overflow-hidden shadow-sm
      hover:cursor-pointer hover:opacity-75
      transition-all duration-300`}
      onClick={() => {
        navigate(`/blog/${post.slug}`);
      }}
    >
      <div className="relative overflow-hidden h-[200px]">
        {post.coverImage ? (
          <img
            src={post.coverImage}
            alt={post.title}
            className="w-full h-full object-cover"
            loading="lazy"
            onError={(e) => {
              const target = e.target as HTMLImageElement;
              target.style.display = "none";
            }}
          />
        ) : null}
      </div>

      <div
        className="pt-3 pb-1 px-1 h-[100px]
      flex flex-col justify-between items-between"
      >
        <p
          className="font-[600] text-light-text dark:text-dark-text mb-2 overflow-hidden"
          style={{
            display: "-webkit-box",
            WebkitLineClamp: 2,
            WebkitBoxOrient: "vertical",
          }}
        >
          {post.title}
        </p>

        <div className="flex items-center justify-between">
          READ MORE
          <LuArrowRight size={16} />
        </div>
      </div>
    </div>
  );
}

function ReadMoreButton() {
  const isCompactView = useIsCompactView();
  const navigate = useNavigate();

  return (
    <div
      id="home-blog-read-more"
      className={`bg-gradient-to-b from-[#EEEFF6] dark:from-[#30353A]
      to-white dark:to-[#222528] p-2 ${isCompactView ? "mx-2" : ""}
      flex flex-col h-[300px]
      rounded-lg overflow-hidden shadow-sm
      hover:cursor-pointer hover:opacity-75
      transition-all duration-300`}
      onClick={() => {
        navigate("/blog");
      }}
    >
      <div className="relative overflow-hidden h-[200px]">
        <img
          src={darvinSeeAllArticles}
          className="w-full h-full object-cover"
          loading="lazy"
        />
      </div>

      <div
        className="pt-3 pb-1 px-1 h-[100px]
      flex flex-col justify-between items-between"
      >
        <p
          className="font-[600] text-light-text dark:text-dark-text mb-2 overflow-hidden"
          style={{
            display: "-webkit-box",
            WebkitLineClamp: 2,
            WebkitBoxOrient: "vertical",
          }}
        >
          See all posts
        </p>

        <div className="flex items-center justify-between">
          SEE ALL
          <LuArrowRight size={16} />
        </div>
      </div>
    </div>
  );
}
